import type { ProcessingChain } from '@/lib/reasoning/chainStatus';
import { findActiveProcessingTask } from '@/lib/reasoning/chainStatus';

import { trackChatRun } from './chatRunTracker';

export interface ResumeActiveRunArgs {
  orgId: string;
  /** The conversation the chains belong to — also the tracker key. */
  conversationId: string;
  chains: ProcessingChain[];
  /** Adopt the run into the surface's own state (task id, progress UI). */
  onResume: (taskId: string) => void;
}

/**
 * Re-adopt a reasoning run that was still in flight when a conversation was
 * (re)loaded — after a page refresh, a back-navigation, or rehydrating a
 * slideout. Finds the chain that is still processing, hands its task id to
 * the caller, and registers the run with the chat run tracker so the nav badge
 * and away-completion / failure toasts keep working. Registration is
 * idempotent, so re-adopting an already-tracked run is safe.
 *
 * Guards ("am I already tracking a task?", "is this response still current?")
 * stay with the caller — they differ per surface.
 *
 * Returns the adopted task id, or null when nothing is in flight.
 */
export function resumeActiveRun({
  orgId,
  conversationId,
  chains,
  onResume,
}: ResumeActiveRunArgs): string | null {
  const taskId = findActiveProcessingTask(chains);
  if (!taskId) {
    return null;
  }
  onResume(taskId);
  trackChatRun({ orgId, conversationId, taskId });
  return taskId;
}
